import {
  CrossCameraApp,
  CrossCameraIdentity,
  CrossCameraLookalikeSearch,
  CrossCameraPipeline,
  CrossCameraTrackingModel
} from '@/types/crossCamera'
import { PaginatedResponse, WithListParams } from '@/types/pagination'
import { vpApi } from './vpApi'

const getCrossCameraAppList = async (params: WithListParams) => {
  const res = await vpApi.get<PaginatedResponse<CrossCameraApp[]>>(
    `/v1/cross-camera/apps/`,
    {
      params
    }
  )

  return res.data
}

export type CrossCameraPipelineWrite = {
  name: string
  application_id: string
  // Left out on edit to keep the current model; null means the catalogue default.
  model_id?: string | null
  similarity_threshold: number
  candidate_retention_s: number
  rejoin_enabled: boolean
  faces_enabled: boolean
  search_min_similarity: number
  min_track_pictures: number
  rejoin_window_s: number
  rejoin_threshold: number
}

const getCrossCameraPipelineList = async (params: WithListParams) => {
  const res = await vpApi.get<PaginatedResponse<CrossCameraPipeline[]>>(
    '/v1/cross-camera/pipelines/',
    { params }
  )

  return res.data
}

const createCrossCameraPipeline = async (data: CrossCameraPipelineWrite) => {
  const res = await vpApi.post<CrossCameraPipeline>(
    '/v1/cross-camera/pipelines/',
    data
  )

  return res.data
}

const updateCrossCameraPipeline = async ({
  pipelineId,
  ...data
}: CrossCameraPipelineWrite & { pipelineId: string }) => {
  const res = await vpApi.patch<CrossCameraPipeline>(
    `/v1/cross-camera/pipelines/${pipelineId}/`,
    data
  )

  return res.data
}

const deleteCrossCameraPipeline = async (pipelineId: string) => {
  return vpApi.delete(`/v1/cross-camera/pipelines/${pipelineId}/`)
}

const restartCrossCameraPipeline = async (pipelineId: string) => {
  const res = await vpApi.post<CrossCameraPipeline>(
    `/v1/cross-camera/pipelines/${pipelineId}/restart/`
  )

  return res.data
}

type CrossCameraIdentityListResponse = {
  count: number
  results: CrossCameraIdentity[]
}

const getCrossCameraIdentityList = async (
  pipelineId: string,
  params: WithListParams
) => {
  const res = await vpApi.get<CrossCameraIdentityListResponse>(
    `/v1/cross-camera/pipelines/${pipelineId}/identities/`,
    { params }
  )

  return res.data
}

const getCrossCameraModels = async () => {
  const res = await vpApi.get<CrossCameraTrackingModel[]>(
    '/v1/cross-camera/models/'
  )

  return res.data
}

export type CrossCameraLookalikeParams = {
  application_id: string
  tracking_id: string
}

const searchCrossCameraLookalikes = async (
  params: CrossCameraLookalikeParams
) => {
  const res = await vpApi.post<CrossCameraLookalikeSearch>(
    '/v1/cross-camera/pipelines/search/',
    params
  )

  return res.data
}

export type CrossCameraLinkParams = CrossCameraLookalikeParams & {
  other_tracking_id: string
}

type CrossCameraLinkResponse = { identity_id: string; changed: number }

const linkCrossCameraTracks = async (params: CrossCameraLinkParams) => {
  const res = await vpApi.post<CrossCameraLinkResponse>(
    '/v1/cross-camera/pipelines/link/',
    params
  )

  return res.data
}

const unlinkCrossCameraTrack = async (params: CrossCameraLookalikeParams) => {
  const res = await vpApi.post<CrossCameraLinkResponse>(
    '/v1/cross-camera/pipelines/unlink/',
    params
  )

  return res.data
}

export type CrossCameraDeletePersonParams = {
  application_id: string
  identity_id: string
}

const deleteCrossCameraPerson = async (
  params: CrossCameraDeletePersonParams
) => {
  const res = await vpApi.post<{ identity_id: string; removed: number }>(
    '/v1/cross-camera/pipelines/delete-person/',
    params
  )

  return res.data
}

export type CrossCameraDeleteAllPeopleParams = { application_id?: string }

const deleteAllCrossCameraPeople = async (
  params: CrossCameraDeleteAllPeopleParams
) => {
  const res = await vpApi.post<{ removed: number; people_deleted: number }>(
    '/v1/cross-camera/pipelines/delete-all-people/',
    params
  )

  return res.data
}

export const crossCameraApi = {
  getCrossCameraAppList,
  getCrossCameraPipelineList,
  createCrossCameraPipeline,
  updateCrossCameraPipeline,
  deleteCrossCameraPipeline,
  restartCrossCameraPipeline,
  getCrossCameraIdentityList,
  getCrossCameraModels,
  searchCrossCameraLookalikes,
  linkCrossCameraTracks,
  unlinkCrossCameraTrack,
  deleteCrossCameraPerson,
  deleteAllCrossCameraPeople
}
